/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: emailSettingsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the emailSettings module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Email Settings module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate an Email Configuration in the form.
 * Pattern: /admin/system-settings/email/new?duplicate={id}
 */
export const getEmailConfigDuplicateUrl = (id: string | number): string =>
  `/admin/system-settings/email/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given Email Configuration id.
 */
export const navigateToEmailConfigDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getEmailConfigDuplicateUrl(id));
};

/**
 * Low-level API helper to delete an Email Configuration by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteEmailConfigById = async (
  id: string | number,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/email-settings/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete Email Configuration';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};

